/**
 * Honeypot Scene (Phase 1)
 * Deceptively cheerful math app with uncanny mascot
 * Duration: 0-60 seconds before transition
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { QuantatoMascot } from '../entities/QuantatoMascot.js';
import { MathQuestion } from '../ui/MathQuestion.js';
import { audioEngine } from '../systems/AudioEngine.js';
import { stateManager } from '../systems/StateManager.js';

export class HoneypotScene extends Phaser.Scene {
    constructor() {
        super({ key: 'HoneypotScene' });
    }

    create() {
        // Record play start
        stateManager.recordPlay();

        // Bright, cheerful background
        this.cameras.main.setBackgroundColor(CONSTANTS.COLORS.BRIGHT_BG);
        this.cameras.main.fadeIn(500);

        // Phase timing
        this.phaseTimer = 0;
        this.PHASE_DURATION = CONSTANTS.HONEYPOT_DURATION;
        this.DREAD_START = CONSTANTS.HONEYPOT_DREAD_START;

        // Tracking
        this.correctAnswers = 0;
        this.wrongAnswers = 0;
        this.dreadLevel = 0;

        // Store UI elements for Matter.js transition
        this.uiElements = [];

        // Create scene elements
        this.createBackground();
        this.createMascot();
        this.createMathUI();
        this.createProgressUI();

        // Input for mascot eye tracking
        this.input.on('pointermove', (pointer) => {
            this.mascot.lookAt(pointer.worldX, pointer.worldY);
        });
    }

    createBackground() {
        const screenWidth = this.scale.width;
        const screenHeight = this.scale.height;
        const centerX = screenWidth / 2;

        // Decorative grid pattern (subtle)
        const graphics = this.add.graphics();
        graphics.lineStyle(1, 0xE0E0E0, 0.5);

        for (let x = 0; x <= screenWidth; x += 40) {
            graphics.moveTo(x, 0);
            graphics.lineTo(x, screenHeight);
        }
        for (let y = 0; y <= screenHeight; y += 40) {
            graphics.moveTo(0, y);
            graphics.lineTo(screenWidth, y);
        }
        graphics.strokePath();

        // Title banner
        this.titleBanner = this.add.graphics();
        this.titleBanner.fillStyle(CONSTANTS.COLORS.CHEERFUL_ACCENT, 0.9);
        this.titleBanner.fillRoundedRect(centerX - 200, 20, 400, 60, 10);
        this.uiElements.push(this.titleBanner);

        this.titleText = this.add.text(centerX, 50, 'Math Time with Quantato!', {
            fontSize: '24px',
            fontFamily: 'Georgia, serif',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);
        this.uiElements.push(this.titleText);
    }

    createMascot() {
        const centerX = this.scale.width / 2;
        // Create mascot with uncanny realistic eyes
        this.mascot = new QuantatoMascot(this, centerX, 200, {
            scale: 1.2,
            isUncanny: true
        });
        this.mascot.setDepth(10);
    }

    createMathUI() {
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height / 2;
        // Math question component
        this.mathQuestion = new MathQuestion(this, centerX, centerY + 120, {
            difficulty: 1,
            onCorrect: () => this.handleCorrectAnswer(),
            onWrong: () => this.handleWrongAnswer()
        });
        this.mathQuestion.setDepth(20);

        // Store for transition
        this.uiElements.push(this.mathQuestion.getContainer());
    }

    createProgressUI() {
        const screenWidth = this.scale.width;
        const screenHeight = this.scale.height;
        const centerX = screenWidth / 2;

        // Score display
        this.scoreText = this.add.text(50, screenHeight - 50, 'Score: 0', {
            fontSize: '18px',
            fontFamily: 'Courier New, monospace',
            color: '#666666'
        });

        // Progress bar background
        this.progressBg = this.add.graphics();
        this.progressBg.fillStyle(0xE0E0E0);
        this.progressBg.fillRoundedRect(centerX - 150, screenHeight - 45, 300, 20, 10);

        // Progress bar fill
        this.progressFill = this.add.graphics();

        // Progress text
        this.progressText = this.add.text(screenWidth - 100, screenHeight - 45, '60s', {
            fontSize: '16px',
            fontFamily: 'Arial',
            color: '#666666'
        });

        this.uiElements.push(this.scoreText);
        this.uiElements.push(this.progressBg);
        this.uiElements.push(this.progressFill);
        this.uiElements.push(this.progressText);
    }

    handleCorrectAnswer() {
        this.correctAnswers++;
        this.updateScore();
        audioEngine.playCorrect();

        // Mascot happy reaction
        this.mascot.blink();

        // Check if should trigger transition early
        if (this.correctAnswers >= CONSTANTS.HONEYPOT_ANSWERS_THRESHOLD) {
            // Can trigger early transition
        }
    }

    handleWrongAnswer() {
        this.wrongAnswers++;
        audioEngine.playWrong();

        // Slight camera shake
        this.cameras.main.shake(150, 0.005);
    }

    updateScore() {
        const score = this.correctAnswers * 100;
        this.scoreText.setText(`Score: ${score}`);
    }

    updateProgress() {
        const elapsed = this.phaseTimer;
        const remaining = Math.max(0, this.PHASE_DURATION - elapsed);
        const remainingSeconds = Math.ceil(remaining / 1000);

        // Update progress bar (use same position as progressBg)
        const centerX = this.scale.width / 2;
        const screenHeight = this.scale.height;
        const progress = elapsed / this.PHASE_DURATION;
        this.progressFill.clear();
        this.progressFill.fillStyle(CONSTANTS.COLORS.CHEERFUL_ACCENT);
        this.progressFill.fillRoundedRect(centerX - 150, screenHeight - 45, 300 * progress, 20, 10);

        // Update time text
        this.progressText.setText(`${remainingSeconds}s`);

        // Change color as time runs out
        if (remainingSeconds <= 10) {
            this.progressText.setColor('#FF6600');
        }
    }

    updateDread() {
        if (this.phaseTimer < this.DREAD_START) return;

        // Calculate dread level (0 to 1 over remaining time)
        const dreadProgress = (this.phaseTimer - this.DREAD_START) /
            (this.PHASE_DURATION - this.DREAD_START);
        this.dreadLevel = Math.min(1, dreadProgress);

        // Apply dread to mascot
        this.mascot.setDread(this.dreadLevel);

        // Subtle visual changes
        this.applyDreadEffects();
    }

    applyDreadEffects() {
        // Darken background slightly
        const bgLerp = this.dreadLevel * 0.3;
        const bgColor = Phaser.Display.Color.Interpolate.ColorWithColor(
            Phaser.Display.Color.ValueToColor(CONSTANTS.COLORS.BRIGHT_BG),
            Phaser.Display.Color.ValueToColor(0x888888),
            100,
            bgLerp * 100
        );
        this.cameras.main.setBackgroundColor(
            Phaser.Display.Color.GetColor(bgColor.r, bgColor.g, bgColor.b)
        );

        // Occasional flicker
        if (this.dreadLevel > 0.5 && Math.random() < 0.01) {
            this.cameras.main.flash(50, 0, 0, 0, true);
        }

        // Title text glitch
        if (this.dreadLevel > 0.7 && Math.random() < 0.02) {
            const centerX = this.scale.width / 2;
            this.titleText.x += Phaser.Math.Between(-3, 3);
            this.time.delayedCall(50, () => {
                this.titleText.x = centerX;
            });
        }
    }

    update(time, delta) {
        this.phaseTimer += delta;

        // Update mascot
        this.mascot.update(delta);

        // Update progress display
        this.updateProgress();

        // Update dread effects
        this.updateDread();

        // Check for transition trigger
        if (this.phaseTimer >= this.PHASE_DURATION) {
            this.triggerTransition();
        }
    }

    triggerTransition() {
        // Prepare data for transition scene
        const transitionData = {
            uiElements: this.collectUIElementsData(),
            score: this.correctAnswers * 100,
            correctAnswers: this.correctAnswers
        };

        // Play transition audio
        audioEngine.playHorrorDrone();

        // Flash and transition
        this.cameras.main.flash(200, 255, 0, 0);

        this.time.delayedCall(300, () => {
            this.scene.start('TransitionScene', transitionData);
        });
    }

    collectUIElementsData() {
        // Collect position and size data for UI elements to be converted to physics
        const centerX = this.scale.width / 2;
        const screenHeight = this.scale.height;
        const elements = [];

        // Title banner area
        elements.push({
            x: centerX,
            y: 50,
            width: 400,
            height: 60,
            type: 'banner'
        });

        // Math question area
        elements.push({
            x: centerX,
            y: screenHeight / 2 + 120,
            width: 400,
            height: 160,
            type: 'question'
        });

        // Score area
        elements.push({
            x: 100,
            y: screenHeight - 45,
            width: 100,
            height: 30,
            type: 'score'
        });

        // Progress bar
        elements.push({
            x: centerX,
            y: screenHeight - 35,
            width: 300,
            height: 20,
            type: 'progress'
        });

        return elements;
    }
}
